import { useCallback, useState } from 'react';
import { ILabel, IValidationRule } from '../constants/interfaces';
import { IFormStateValue } from '../utils/formUtils';
import { validateConfirmationRules } from '../utils/validateConfirmationRules';
import { validateApiRules } from '../utils/validateApiRules';

export function useConfirmationValidation(
  setSnackbar?: (snackbar: {
    message: string;
    open: boolean;
    severity: 'success' | 'error';
  }) => void
) {
  const [showModal, setShowModal] = useState(false);
  const [validationResponse, setValidationResponse] = useState<IValidationRule>(
    {
      canContinue: false,
      message: '',
      shouldReset: false,
      title: '',
    }
  );
  const [isValidating, setIsValidating] = useState(false);

  const validate = useCallback(
    async (
      formState: Record<string, IFormStateValue>,
      rules: unknown,
      labels?: ILabel[]
    ) => {
      if (!rules) return true;

      setIsValidating(true);

      try {
        const confirmationRules =
          typeof rules === 'string' ? JSON.parse(rules) : rules;

        // First validate regular confirmation rules
        const { isValid: regularValid, error: regularError } =
          validateConfirmationRules(
            formState,
            confirmationRules.action_required,
            labels
          );
        if (!regularValid) {
          setValidationResponse(regularError);
          setShowModal(true);
          return false;
        }

        // Then validate API rules if they exist
        if (
          confirmationRules.api_rules &&
          confirmationRules.api_rules.length > 0
        ) {
          const { isValid: apiValid, error: apiError } = await validateApiRules(
            formState,
            confirmationRules.api_rules
          );
          if (!apiValid && apiError) {
            // Check if error should be shown as toast notification
            if (apiError.showAsToast && setSnackbar) {
              setSnackbar({
                message: apiError.message,
                open: true,
                severity: 'error',
              });
              return false;
            } else {
              // Show as modal (default behavior)
              setValidationResponse(apiError);
              setShowModal(true);
              return false;
            }
          }
        }

        return true;
      } catch (error) {
        console.error('Validation error:', error);
        setValidationResponse({
          canContinue: false,
          message: 'An error occurred during validation. Please try again.',
          shouldReset: false,
          title: 'Validation Error',
        });
        setShowModal(true);
        return false;
      } finally {
        setIsValidating(false);
      }
    },
    [setSnackbar]
  );

  return {
    isValidating,
    setShowModal,
    setValidationResponse,
    showModal,
    validate,
    validationResponse,
  };
}
